import { useEffect, useState, useMemo } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const HumanRights = () => {
  const navigate = useNavigate();
  const itemId = "human-rights";
  const fieldOrder = useMemo(() => ["date", "employeeName", "employeeSignature"], []);
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    }
  }, [fieldOrder]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   return (
  //     input.getFullYear() === now.getFullYear() &&
  //     input.getMonth() === now.getMonth() &&
  //     input.getDate() === now.getDate()
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD"
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const statusToPersist = allEmpty ? "incomplete" : (existing === "complete" ? "complete" : "in-progress");
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (accountId && statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData, fieldOrder]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr("");
    setFieldErrors({});

    const errors = {};
    if (!formData.date) errors.date = "This field is required";
    else if (!isDateToday(formData.date)) errors.date = "Date must be today";
    if (!String(formData.employeeName || "").trim()) errors.employeeName = "This field is required";
    if (!formData.employeeSignature) errors.employeeSignature = "Signature is required";

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      return;
    }

    const accountId = localStorage.getItem("accountId");
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const dateValid = isDateToday(formData.date);
    const signatureValid = !!(formData.employeeSignature && String(formData.employeeSignature).length > 0);
    const canBeComplete = allFilled && dateValid && signatureValid;
    const newStatus = allEmpty ? "incomplete" : canBeComplete ? "complete" : "in-progress";
    const payload = { status: newStatus, formData };
    if (accountId && newStatus === 'complete') {
      axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
        .catch(err => console.error('Failed to save human-rights item:', err))
        .finally(() => {
          axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} }).catch(() => {});
          showSuccess("Form completed successfully!");
          navigate('/register');
        });
    } else {
      navigate('/register');
    }
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (field === "date") {
        if (value && isDateToday(value)) delete next[field];
      } else {
        if (String(value || "").trim()) delete next[field];
      }
      return next;
    });
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        {/* Top content card */}
        <div className="form-card">
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
            <h2 className="form-title" style={{ textAlign: "center" }}>
              Human Rights Code
            </h2>
          </header>

          <section className="form-card-content">
            {err && <div className="error-text" style={{ color: "#dc2626", marginBottom: 12 }}>{err}</div>}
            <p className="form-intro">
              In compliance with training requirements as set out in Section 7 of the IASR, all employees must be trained on the Human Rights Code as it pertains to persons with disabilities. The following information is also available in video format at:
            </p>
            <p className="form-intro">
                <a href="https://www.ohrc.on.ca/en/online-learning/working-together-code-and-aoda" class="red-link" target="_blank" rel="noopener noreferrer">
                    http://www.ohrc.on.ca/en/learning/working-together-code-and-aoda
                </a>
            </p>
            <p className="form-intro">
                The Code and the Accessibility for Ontarians with Disabilities Act (AODA) work together in various ways to promote quality and accessibility. As part of the AODA, the Government of Ontario is developing and enforcing mandatory province-wide accessibility standards in key areas of daily life. The first regulation under the AODA was the customers service standard. The next three standards – Information and Communications, Employment and Transportation – are contained in one regulation called the Integrated Accessibility Standards Regulation or IASR for short. The Code has primacy. It overrides the AODA and other provincial laws when there is a conflict. The Code offers protection of rights, equal opportunity, and freedom from discrimination. It applies to jobs, housing, and services, and states that employers, landlords, and service providers must accommodate people with disabilities to the point of undue hardship.
            </p>
            <p className="form-intro">
                Employers must accommodate employees when they cannot perform all of the duties of the job because of their disabilities. For example, suppose an employee with low vision has a job requiring her to work on a computer. Accommodating her needs might include supplying appropriate computer technology.
            </p>
            <p className="form-intro">
                Disabilities can include physical limitations, mental health, cognitive or intellectual development, learning, hearing, or vision. They also can include epilepsy, substance addictions, environmental sensitivities, and workplace injuries. Disability itself is not a barrier, but barriers exist that can exclude people with disabilities. These include physical barriers, information and communication barriers, systemic barriers, or attitudinal barriers.
            </p>
            <p className="form-intro topic1">
              Barriers to Accessibility
            </p>
            <table class="custom-table">
                <thead>
                    <tr>
                        <th>Type of Barriers</th>
                        <th>Examples</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Attitudinal barriers - discriminate against people with disabilities.</td>
                        <td>
                            <ul className="form-bullets">
                                <li>• thinking that people with disabilities are inferior</li>
                                <li>• assuming that a person who has a speech impairment can't understand you</li>
                            </ul>
                        </td>
                    </tr>
                    <tr>
                        <td>Information or communication barriers - when a person can't easily understand information.</td>
                        <td>
                            <ul className="form-bullets">
                                <li>• websites that can't be accessed by people who are not able to use a mouse</li>
                            </ul>
                        </td>
                    </tr>
                    <tr>
                        <td>Technology barriers - when a technology can't be modified to support various assistive devices.</td>
                        <td>
                            <ul className="form-bullets">
                                <li>• a website that doesn't support screen-reading software</li>
                            </ul>
                        </td>
                    </tr>
                    <tr>
                        <td>Organizational barriers - an organization's policies, practices or procedures that discriminate against people with disabilities.</td>
                        <td>
                            <ul className="form-bullets">
                                <li>• a hiring process that is not open to people with disabilities</li>
                            </ul>
                        </td>
                    </tr>
                    <tr>
                        <td>Architectural and physical barriers - features of buildings or spaces that cause problems for people with disabilities.</td>
                        <td>
                            <ul className="form-bullets">
                                <li>• hallways and doorways that are too narrow for a person using a wheelchair, electric scooter or walker.</li>
                            </ul>
                        </td>
                    </tr>
                </tbody>
            </table>
            <p className="form-intro">
              <strong>Accommodation</strong> means not denying people jobs or services in the first place if they can be accommodated to the point of undue hardship. The most appropriate accommodation is the one that best meets the individual needs of the person with a disability. You are only exempt if it would cause undue hardship. Undue hardship is the legal limit of the duty to accommodate. It refers to situations where severe negative effects outweigh the benefit of providing accommodation. Three factors are used to determine undue hardship:
            </p>
            <p className="form-intro">
              The courts have set a very high standard of proof. You must provide clear evidence if you are claiming undue hardship. Employers have a duty to accommodate the employee as fully and as promptly as possible in his or her job. Modifying job duties might be appropriate in some cases. If accommodation in the original job isn’t possible or would cause undue hardship, consider other available jobs or re-training opportunities that could accommodate the employee in another job.
            </p>
            <p className="form-intro paragraph-space">
              If you need an accommodation:
            </p>
            <ul className="form-bullets">
              <li>• Tell your employer, union, landlord, or service provider about any needs related to your disability and your job duties, tenancy, or the services being provided.</li>
              <li>• Provide supporting information about your disability-related needs, including medical or other expert opinions where required.</li>
              <li>• Get involved in seeking accommodation solutions to the best of your ability.</li>
            </ul>
            <p className="form-intro paragraph-space">
              {" "}
            </p>
            <p className="form-intro paragraph-space">
              An employer, union, landlord, or service provider must:
            </p>
            <ul className="form-bullets">
              <li>• Accept requests for accommodation in good faith.</li>
              <li>• Act promptly, even if it means creating a temporary solution before a long-term one can be put in place.</li>
              <li>• Ask only for information needed to provide the accommodation. For example, you may need to know that someone with vision loss is unable to read printed material, but not how or why she lost her vision.</li>
              <li>• Actively seek appropriate accommodation solutions and ask for expert help if needed. - Cooperate with other parties where necessary.</li>
              <li>• Respect the dignity and privacy of the person asking for accommodation, and make sure the accommodation process doesn’t lead to reprisals against that person. - Cover the costs of accommodations, such as any needed medical or other expert opinions or documents, to the point of undue hardship.</li>
            </ul>
            <p className="form-intro paragraph-space">
              {" "}
            </p>
            <p className="form-intro paragraph-space">
              Organizations must set up processes to address employee accommodation needs at all stages of their employment. Except for small organizations, employers must have a written process to document an employee’s accommodation needs in an individual accommodation plan.
            </p>
            <p className="form-intro paragraph-space">
              Ontario has three organizations in the human rights system:
            </p>
            <ul className="form-bullets">
              <li>• <strong>The Human Rights Commission</strong> provides policies, guidelines and other information on Code grounds, including disability and the duty to accommodate.</li>
              <li>• <strong>The Human Rights Tribunal</strong> hears discrimination claims on any Code ground. This includes claims from individuals who believe an organization or person has failed to accommodate disability-related needs.</li>
              <li>• <strong> The Human Rights Legal Support Centre</strong> helps people through the human rights process, such as completing an application or claim to the Tribunal.</li>
            </ul>
            <p className="form-intro paragraph-space" style={{ marginBottom: "3rem" }}>
              Individuals cannot file accessibility complaints under the AODA. But in cases of non-compliance, the Directorate can enforce standards through mechanisms set under the law. Meanwhile, the Accessibility Directorate works with organizations having trouble meeting the standards in order to bring them into compliance.
            </p>
            <p className="form-intro paragraph-space">
              By signing below, I acknowledge that I have read and understand the information above regarding the Human Rights Code as it relates to people with disabilities.
            </p>
            
          </section>
          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
              {fieldErrors.date && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.date}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="employeeName">Employee's Full Name*</label>
              <input
                id="employeeName"
                type="text"
                value={formData.employeeName}
                onChange={(e) =>
                  handleInputChange("employeeName", e.target.value)
                }
                required
              />
              {fieldErrors.employeeName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeName}</div>}
            </div>
          </div>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            {/* <label>Employee's Signature*</label> */}
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeSignature}</div>}
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit}>Submit</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HumanRights;
